/**
 * Demo-Shell — isoliert, kein AMZEL-Nest-Branding.
 *
 * Diese Demo läuft eigenständig unter /demo/voice-ai und wird im Strategic
 * Call gezeigt. Bewusst nicht in den bestehenden ShellV2 eingebettet —
 * der Demo soll später ggf. auf eine eigene Subdomain (demo.amzel.io)
 * extrahierbar sein, deshalb hier komplett standalone.
 *
 * Inhaltlich werden in den Scenes ausschliesslich generische Site-Labels
 * verwendet (Site Alpha / Beta / Gamma — siehe demo-constants.ts), keine
 * realen Site-Codes oder Stadtnamen.
 *
 * Bestehende Tours, app.amzel.io und das amzel-website-Projekt bleiben
 * unangetastet.
 */
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import "../i18n";
import { ThemeProvider, ThemeToggle } from "../theme-provider";

interface ShellDemoProps {
  children: ReactNode;
}

export function ShellDemo({ children }: Readonly<ShellDemoProps>) {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 text-slate-900 transition-colors dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950/30 dark:text-slate-100">
        <DemoHeader />
        <main className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-8 sm:py-10">
          {children}
        </main>
        <DemoFooter />
      </div>
    </ThemeProvider>
  );
}

function DemoHeader() {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-4 px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <img src="/amzel-logo.svg" alt="AMZEL" className="size-10 shrink-0" />
          <div className="flex flex-col leading-tight">
            <span className="text-base font-semibold tracking-tight">AMZEL Voice-AI</span>
            <span className="text-xs text-emerald-700 dark:text-emerald-400">
              Strategic Demo · Private Preview
            </span>
          </div>
        </div>
        <span className="ml-auto rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
          {t("demo.privateBadge", { defaultValue: "Private Preview" })}
        </span>
        <ThemeToggle />
      </div>
    </header>
  );
}

function DemoFooter() {
  return (
    <footer className="mt-16 border-t border-slate-200 bg-white/60 dark:border-slate-800 dark:bg-slate-950/60">
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-2 px-4 sm:px-6 py-6 sm:flex-row">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          AMZEL Voice-AI Demo · prepared by Spreenauten GmbH
        </div>
        <div className="text-xs text-slate-400 dark:text-slate-500">
          Private preview · do not redistribute
        </div>
      </div>
    </footer>
  );
}
